import { type ReactNode } from 'react';
import type { GridApi } from '@ag-grid-community/core';
import { Button, Icon } from '@ics-portal/guardian';
import type { ICSAgGridProps } from '../../ICSAgGrid';
import { ColumnDropdown } from './ColumnDropdown';
import { DateDropdown } from './DateDropdown';
import { DateFilterOptionDropdown } from './DateFilterOptionDropdown';
import { ValueDropdown } from './ValueDropdown';
import { useGridSearch } from './useGridSearch';
import { getPlaceholder, NUMBER_FILTER_OPTIONS } from './utils';
import s from './grid-search.module.scss';

type ICSGridSearchProps<TData> = Partial<ICSAgGridProps<TData>> & {
    api?: GridApi<TData>;
};

export function ICSGridSearch<TData>(
    props: ICSGridSearchProps<TData>,
): ReactNode {
    const {
        inputRef,
        containerRef,
        appliedFilters,
        pendingColumn,
        pendingDateOption,
        pendingNumberOption,
        inputValue,
        showColumnDropdown,
        showValueDropdown,
        showDateOptionDropdown,
        showDateDropdown,
        showNumberOptionDropdown,
        selectDateOption,
        selectNumberOption,
        grouped,
        filteredValueOptions,
        handleInputChange,
        handleInputFocus,
        handleKeyDown,
        handleColumnOptionKeyDown,
        handleValueOptionKeyDown,
        handleDateOptionKeyDown,
        handleNumberOptionKeyDown,
        removeFilter,
        commitOption,
        selectColumn,
    } = useGridSearch(props);

    return (
        <div ref={containerRef} className={s.searchContainer}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click delegates focus to input */}
            <div
                className={s.searchBar}
                onClick={() => inputRef.current?.focus()}
            >
                <Icon
                    fontSize="medium"
                    name="search"
                    className={s.searchIcon}
                    aria-hidden="true"
                />

                {appliedFilters.map((filter) => (
                    <span key={filter.id} className={s.filterChip}>
                        {filter.column ? (
                            <span className={s.chipColumn}>
                                {filter.column.headerName}:&nbsp;
                            </span>
                        ) : null}
                        <span>{filter.label ?? filter.value}</span>
                        <Button
                            icon={<Icon name="close" />}
                            className={s.chipRemove}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeFilter(filter.id);
                            }}
                            aria-label={`Remove filter: ${filter.column ? `${filter.column.headerName}: ` : ''}${filter.label ?? filter.value}`}
                        />
                    </span>
                ))}

                {pendingColumn ? (
                    <span className={`${s.filterChip} ${s.pendingChip}`}>
                        <span className={s.chipColumn}>
                            {pendingColumn.headerName}:
                        </span>
                        {pendingDateOption ? (
                            <span>&nbsp;{pendingDateOption.label}</span>
                        ) : null}
                        {pendingNumberOption ? (
                            <span>&nbsp;{pendingNumberOption.label}</span>
                        ) : null}
                    </span>
                ) : null}

                <input
                    ref={inputRef}
                    id={props.id ? `${props.id}_search` : undefined}
                    type={
                        pendingColumn?.inputType === 'number'
                            ? 'number'
                            : 'text'
                    }
                    className={s.searchInput}
                    value={inputValue}
                    placeholder={getPlaceholder(pendingColumn)}
                    autoComplete="off"
                    aria-label="Search table"
                    onChange={(e) => {
                        handleInputChange(e.target.value);
                    }}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                />
            </div>

            {showColumnDropdown ? (
                <ColumnDropdown
                    grouped={grouped}
                    onSelect={selectColumn}
                    onKeyDown={handleColumnOptionKeyDown}
                />
            ) : null}

            {showValueDropdown ? (
                <ValueDropdown
                    options={filteredValueOptions}
                    pendingColumn={pendingColumn ?? undefined}
                    onSelect={commitOption}
                    onKeyDown={handleValueOptionKeyDown}
                />
            ) : null}

            {showDateOptionDropdown ? (
                <DateFilterOptionDropdown
                    onSelect={selectDateOption}
                    onKeyDown={handleDateOptionKeyDown}
                />
            ) : null}

            {showNumberOptionDropdown ? (
                <DateFilterOptionDropdown
                    options={NUMBER_FILTER_OPTIONS}
                    onSelect={selectNumberOption}
                    onKeyDown={handleNumberOptionKeyDown}
                />
            ) : null}

            {showDateDropdown ? <DateDropdown onSelect={commitOption} /> : null}
        </div>
    );
}
